import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'

/** Check-in steps whose progress the health system tracks; the names are the keys the check-in steps endpoint accepts. */
export type ReportedCheckInStep =
    | 'personal_information'
    | 'conditions'
    | 'medications'
    | 'allergies'
    | 'consents'
    | 'emergency_contact'
    | 'preferred_pharmacy'

export type CheckInStepStatus = 'in_progress' | 'completed'

/** The check-in sections the health system does not need the patient to fill in for a visit. */
export interface VisitNotNeededCheckInSections {
    not_needed_sections: { demographics: boolean, emergency_contact: boolean }
}

export const visitCheckInStepsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getVisitCheckInSteps: builder.query<VisitNotNeededCheckInSections, string>({
            query: (visitId) => apiEndpoints.visits.checkInSteps(visitId),
            providesTags: (_result, _error, visitId) => [{ type: 'VisitCheckInSteps', id: visitId }],
            // The answer is read once per visit and kept: the health system changes a step's status as the patient
            // reports progress, which would otherwise make a section that is not needed reappear partway through check-in.
            keepUnusedDataFor: 3600,
        }),
        updateVisitCheckInSteps: builder.mutation<
            { steps: ReportedCheckInStep[], status: CheckInStepStatus },
            { visitId: string, steps: ReportedCheckInStep[], status: CheckInStepStatus }
        >({
            query: ({ visitId, steps, status }) => ({
                url: apiEndpoints.visits.checkInSteps(visitId),
                method: 'PUT',
                body: { steps, status },
            }),
            // Reporting progress changes the statuses the not-needed sections come from, so the cached answer is kept.
            invalidatesTags: [],
        }),
    }),
    overrideExisting: false,
})

export const { useGetVisitCheckInStepsQuery, useUpdateVisitCheckInStepsMutation } = visitCheckInStepsApi
